import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectUser } from '../../store/user/user.selector';
import { setSystemNotification } from '../../store/system/system.action';
import { api } from '../../api/rtk';
import { listenToEbayCards, removeCardFromEbay, addCustomCardToEbay, updateEbayItem, createEbayLot, ungroupEbayLot, ensureEbaySkus } from '../../utils/firebase/ebay';
import { promoteEbayItemsToSales } from '../../utils/firebase/ebayToSales';
import { reassignStagedItems } from '../../utils/firebase/stagedQueue';
import { useEbayPrefs } from '../../hooks/useEbayPrefs';
import { buildEbayTitle, buildEbayDescription, buildEbayLotDescription, EBAY_CONDITIONS, EBAY_CATEGORIES, EBAY_LOTS_CATEGORY_ID, getSportDisplay, getLeague, isAutograph, getEbayCardName, buildLotItemSpecifics } from '../../utils/ebay/ebayListingText';
import { buildEbaySearchUrl, buildEbayResearchUrl } from '../../utils/ebay/buildEbaySearchUrl';
import { buildTitle, isLotItem, isSingleItem } from '../../utils/whatnot/whatnotListingText';
import { itemMatchesPlayerSearch } from '../../utils/ebay/ebayQueueSearch';
import { sumItemPrices } from '../../utils/ebay/ebayLotPrice';
import { IconPlus, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import MultiResponseInput from '../../components/inputs/multi-response-input/MultiResponseInput';
import InputBar from '../../components/input-bar/InputBar';
import CardAttribute from '../../components/card-attribute/CardAttribute';
import InputDropdown from '../../components/input-dropdown/InputDropdown';
import PrimaryBtn from '../../components/btns/primary-btn/PrimaryBtn';

import {
    EbayPageContainer,
    ConfigPanel,
    ConfigFieldsContainer,
    ConfigActions,
    AddCardBtn,
    ExportBtn,
    ItemCount,
    QueueHeader,
    QueueHeaderActions,
    CombineLotBtn,
    DescriptionPreview,
    DescriptionPreviewLabel,
    EmptyState,
    LoadingState,
    ModalOverlay,
    ModalCard,
    ModalActions,
    ModalCancelBtn,
    ModalCreateBtn,
    ModalFieldsGrid,
    PriceInput,
    FormatToggle,
    FormatToggleConfig,
} from './EbayPage.styles';


import ebayLogo from '../../assets/system/logos/ebay-logo.png';

/* ─── CSV ─────────────────────────────────────────────────────────────────── */

const CSV_ACTION_HEADER = 'Action(SiteID=US|Country=US|Currency=USD|Version=1193|CC=UTF-8)';

const CSV_HEADERS = [
    CSV_ACTION_HEADER,
    'Custom label (SKU)',
    'Category ID',
    'Title',
    'UPC',
    'Price',
    'Quantity',
    'Item photo URL',
    'Condition ID',
    'Description',
    'Format',
    'C:Sport',
    'C:Player/Athlete',
    'C:Manufacturer',
    'C:Parallel/Variety',
    'C:Set',
    'C:Team',
    'C:League',
    'C:Autograph',
    'C:Card Name',
    'C:Card Number',
    'C:Print Run',
];

function csvEscape(value) {
    const str = value == null ? '' : String(value);
    return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
}

function generateEbayCsv(items) {
    const infoRows = [
        '#INFO,Version=0.0.2,Template= eBay-draft-listings-template_US,,,,,,,,',
        '#INFO Action and Category ID are required fields. 1) Set Action to Draft 2) Please find the category ID for your listings here: https://pages.ebay.com/sellerinformation/news/categorychanges.html,,,,,,,,,,',
        '"#INFO After you\'ve successfully uploaded your draft from the Seller Hub Reports tab, complete your drafts to active listings here: https://www.ebay.com/sh/lst/drafts",,,,,,,,,,',
        '#INFO,,,,,,,,,,',
    ];

    const headerRow = CSV_HEADERS.map(csvEscape).join(',');

    const dataRows = items.map((item) => {
        if (isLotItem(item)) {
            const specifics = buildLotItemSpecifics(item.cards ?? []);
            const row = [
                'Draft',
                item.sku ?? '',
                EBAY_LOTS_CATEGORY_ID,
                item.title ?? 'Untitled Lot',
                '',
                item.price ?? '1',
                '1',
                '',
                item.conditionId ?? '2750',
                buildEbayLotDescription(item.cards ?? []),
                item.format ?? 'FixedPrice',
                specifics.sport,
                specifics.players,
                specifics.manufacturer,
                specifics.parallel,
                specifics.set,
                specifics.team,
                specifics.league,
                specifics.autograph,
                specifics.cardName,
                specifics.cardNumber,
                specifics.printRun,
            ];
            return row.map(csvEscape).join(',');
        }

        const players = Array.isArray(item.name) ? item.name : (item.name ? [item.name] : []);
        const teams = Array.isArray(item.team) ? item.team : (item.team ? [item.team] : []);
        const parallel = item.cardParallelName && item.cardParallelName.toUpperCase() !== 'BASE'
            ? item.cardParallelName
            : '';

        const row = [
            'Draft',
            item.sku ?? '',
            item.categoryId ?? '261328',
            buildEbayTitle(item, item.series),
            '',
            item.price ?? '1',
            '1',
            '',
            item.conditionId ?? '2750',
            buildEbayDescription(item, item.series),
            item.format ?? 'FixedPrice',
            // Item Specifics
            getSportDisplay(item.sport),
            players.join('|'),
            item.manufacturer ?? '',
            parallel,
            item.series ?? '',
            teams.join('|'),
            getLeague(item.sport),
            isAutograph(item.cardType) ? 'Yes' : 'No',
            getEbayCardName(item.cardType),
            item.cardNumber ?? '',
            item.cardSupply ?? '',
        ];
        return row.map(csvEscape).join(',');
    });

    return [...infoRows, headerRow, ...dataRows].join('\n');
}

function downloadCsv(csv, filename) {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    link.click();
    URL.revokeObjectURL(url);
}

/* ─── Inline listing panel ────────────────────────────────────────────────── */

const ListingPanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

const ListingRowContainer = styled.div`
    align-items: center;
    background: ${({ $selected }) => $selected ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.04)'};
    border: 1px solid ${({ $selected }) => $selected ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)'};
    border-radius: 10px;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    gap: 10px;
    padding: 10px 14px;
    transition: background 0.15s ease, border-color 0.15s ease;
    width: 100%;

    &:hover { background: rgba(255,255,255,0.07); }
`;

const ListingSwatch = styled.div`
    background: ${({ $color }) => $color ?? '#fff'};
    border-radius: 3px;
    flex-shrink: 0;
    height: 28px;
    width: 6px;
`;

const ListingLotBadge = styled.button`
    align-items: center;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    height: 32px;
    justify-content: center;
    padding: 0;
    transition: all 0.15s ease;
    width: 24px;

    &:hover {
        background: rgba(255, 255, 255, 0.14);
        color: #fff;
    }
`;

const ListingNestedRow = styled.div`
    align-items: center;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    display: flex;
    gap: 10px;
    padding: 6px 12px;
`;

const LotCardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 4px;
    margin-left: 28px;
    width: calc(100% - 28px);
`;

const ListingInfo = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const ListingName = styled.div`
    color: rgba(255,255,255,0.9);
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RowActions = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: 8px;
`;

const PriceDollar = styled.span`
    color: rgba(255,255,255,0.4);
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
`;

function buildParallelText(item) {
    const name = item.cardParallelName ?? 'BASE';
    if (item.cardPrintNumber != null && item.cardSupply != null)
        return `${name} #${item.cardPrintNumber}/${item.cardSupply}`;
    if (item.cardSupply != null)
        return `${name} /${item.cardSupply}`;
    return name;
}

function NestedCardRow({ card }) {
    const parallelText = buildParallelText(card);
    return (
        <ListingNestedRow>
            <ListingSwatch $color={card.cardColor ?? '#fff'} />
            <ListingInfo>
                <ListingName>{buildTitle(card)}</ListingName>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {card.series && <CardAttribute text={card.series} size="S" />}
                    {card.cardNumber && <CardAttribute text={`#${card.cardNumber}`} size="S" />}
                    <CardAttribute text={parallelText} color={card.cardColor ?? null} size="S" />
                </div>
            </ListingInfo>
        </ListingNestedRow>
    );
}

function LotRow({ item, isSelected, onToggleSelect, onRemove, onUngroup, onUpdatePrice, onToggleFormat }) {
    const [expanded, setExpanded] = useState(false);
    const cardCount = item.cards?.length ?? item.cardCount ?? 0;
    const isAuction = item.format === 'Auction';

    const toggleExpand = (e) => {
        e.stopPropagation();
        setExpanded((prev) => !prev);
    };

    return (
        <>
            <ListingRowContainer $selected={isSelected} onClick={() => onToggleSelect(item.id)}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(item.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ accentColor: 'rgba(255,255,255,0.85)', cursor: 'pointer', flexShrink: 0, height: 15, width: 15 }}
                />
                <ListingLotBadge onClick={toggleExpand} title={expanded ? 'Collapse' : 'Expand'}>
                    {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ListingLotBadge>
                <ListingInfo>
                    <ListingName>{item.title ?? 'Untitled Lot'}</ListingName>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <CardAttribute text="LOT" size="S" />
                        <CardAttribute text={`${cardCount} ${cardCount === 1 ? 'card' : 'cards'}`} size="S" />
                        {item.sku && <CardAttribute text={item.sku} size="S" />}
                    </div>
                </ListingInfo>
                <RowActions onClick={(e) => e.stopPropagation()}>
                    <CardAttribute text="UNGROUP" onClick={(e) => { e.stopPropagation(); onUngroup(item); }} />
                    <PriceDollar>$</PriceDollar>
                    <PriceInput
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price ?? '1'}
                        onChange={(e) => onUpdatePrice(item.id, e.target.value)}
                        title="Listing price"
                    />
                    <FormatToggle
                        $auction={isAuction}
                        onClick={() => onToggleFormat(item.id, isAuction ? 'FixedPrice' : 'Auction')}
                        title={isAuction ? 'Switch to Fixed Price' : 'Switch to Auction'}
                    >
                        {isAuction ? 'Auction' : 'Fixed'}
                    </FormatToggle>
                    <CardAttribute
                        text="REMOVE"
                        color="#ef4444"
                        onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
                    />
                </RowActions>
            </ListingRowContainer>
            {expanded && (
                <LotCardList>
                    {(item.cards ?? []).map((card, index) => (
                        <NestedCardRow key={`${item.id}-${index}`} card={card} />
                    ))}
                </LotCardList>
            )}
        </>
    );
}

function ListingRow({ item, isSelected, onToggleSelect, onRemove, onUpdatePrice, onToggleFormat, onSoldListings, onProductResearch }) {
    const playerName = Array.isArray(item.name) ? item.name.join(' & ') : (item.name ?? '');
    const parallelText = buildParallelText(item);
    const isAuction = item.format === 'Auction';

    return (
        <ListingRowContainer $selected={isSelected} onClick={() => onToggleSelect(item.id)}>
            <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(item.id)}
                onClick={(e) => e.stopPropagation()}
                style={{ accentColor: 'rgba(255,255,255,0.85)', cursor: 'pointer', flexShrink: 0, height: 15, width: 15 }}
            />
            <ListingSwatch $color={item.cardColor ?? '#fff'} />
            <ListingInfo>
                <ListingName>{playerName}</ListingName>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {item.series && <CardAttribute text={item.series} size="S" />}
                    {item.cardNumber && <CardAttribute text={`#${item.cardNumber}`} size="S" />}
                    <CardAttribute text={parallelText} color={item.cardColor ?? null} size="S" />
                    {item.sku && <CardAttribute text={item.sku} size="S" />}
                </div>
            </ListingInfo>
            <RowActions onClick={(e) => e.stopPropagation()}>
                <PriceDollar>$</PriceDollar>
                <PriceInput
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.price ?? '1'}
                    onChange={(e) => onUpdatePrice(item.id, e.target.value)}
                    title="Listing price"
                />
                <FormatToggle
                    $auction={isAuction}
                    onClick={() => onToggleFormat(item.id, isAuction ? 'FixedPrice' : 'Auction')}
                    title={isAuction ? 'Switch to Fixed Price' : 'Switch to Auction'}
                >
                    {isAuction ? 'Auction' : 'Fixed'}
                </FormatToggle>
                <CardAttribute
                    text="SOLD LISTINGS"
                    color="#10b981"
                    onClick={(e) => { e.stopPropagation(); onSoldListings(item); }}
                />
                <CardAttribute
                    text="PRODUCT RESEARCH"
                    color="#6366f1"
                    onClick={(e) => { e.stopPropagation(); onProductResearch(item); }}
                />
                <CardAttribute
                    text="REMOVE"
                    color="#ef4444"
                    onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
                />
            </RowActions>
        </ListingRowContainer>
    );
}

function ListingPanel({ items, onRemove, onUngroup, onUpdatePrice, onToggleFormat, selectedIds, onToggleSelect, onSoldListings, onProductResearch }) {
    return (
        <ListingPanelContainer>
            {items.map((item) => (
                isLotItem(item) ? (
                    <LotRow
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.has(item.id)}
                        onToggleSelect={onToggleSelect}
                        onRemove={onRemove}
                        onUngroup={onUngroup}
                        onUpdatePrice={onUpdatePrice}
                        onToggleFormat={onToggleFormat}
                    />
                ) : (
                    <ListingRow
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.has(item.id)}
                        onToggleSelect={onToggleSelect}
                        onRemove={onRemove}
                        onUpdatePrice={onUpdatePrice}
                        onToggleFormat={onToggleFormat}
                        onSoldListings={onSoldListings}
                        onProductResearch={onProductResearch}
                    />
                )
            ))}
        </ListingPanelContainer>
    );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

const BLANK_CARD = { series: '', name: [], team: [], cardNumber: '', cardParallelName: '', serial: '', cardAttributes: [] };

function parseSerial(raw) {
    const str = raw.trim();
    if (!str) return { cardPrintNumber: null, cardSupply: null };
    const parts = str.split('/');
    const printNum = parts[0] ? Number(parts[0].trim()) : null;
    const supply = parts[1] ? Number(parts[1].trim()) : null;
    return {
        cardPrintNumber: !isNaN(printNum) && printNum != null ? printNum : null,
        cardSupply: !isNaN(supply) && supply != null ? supply : null,
    };
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export default function EbayPage({ playerSearch = '', onSelectionChange, onListTotalChange }) {
    const user = useSelector(selectUser);
    const dispatch = useDispatch();

    const {
        categoryId, conditionId, defaultFormat, defaultPrice,
        setCategoryId, setConditionId, setDefaultFormat, setDefaultPrice,
    } = useEbayPrefs();

    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [removePending, setRemovePending] = useState(false);
    const [listedPending, setListedPending] = useState(false);
    const [isMarkingListed, setIsMarkingListed] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Add card modal
    const [addFormOpen, setAddFormOpen] = useState(false);
    const [newCard, setNewCard] = useState(BLANK_CARD);
    const [isAdding, setIsAdding] = useState(false);

    // Combine lot modal
    const [combineModalOpen, setCombineModalOpen] = useState(false);
    const [lotTitle, setLotTitle] = useState('');
    const [isCombining, setIsCombining] = useState(false);

    const visibleItems = useMemo(
        () => items.filter((item) => itemMatchesPlayerSearch(item, playerSearch)),
        [items, playerSearch]
    );
    const listTotal = useMemo(() => Number(sumItemPrices(items)), [items]);
    const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((item) => selectedIds.has(item.id));
    const selectedSingles = useMemo(
        () => items.filter((item) => selectedIds.has(item.id) && isSingleItem(item)),
        [items, selectedIds]
    );
    const combinePreviewDescription = useMemo(
        () => buildEbayLotDescription(selectedSingles),
        [selectedSingles]
    );
    const canCombine = selectedSingles.length >= 2;

    useEffect(() => {
        setRemovePending(false);
        setListedPending(false);
    }, [selectedIds]);

    /* ── Data listener ── */
    useEffect(() => {
        if (!user?.uid) { setIsLoading(false); return; }
        setIsLoading(true);
        const unsub = listenToEbayCards(user.uid, (cards) => {
            setItems(cards);
            setIsLoading(false);
        });
        return () => unsub();
    }, [user?.uid]);

    /* ── Selection ── */
    const handleToggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const visibleIds = visibleItems.map((i) => i.id);
            const allChecked = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
            if (allChecked) {
                const next = new Set(prev);
                visibleIds.forEach((id) => next.delete(id));
                return next;
            }
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return next;
        });
    }, [visibleItems]);

    useEffect(() => {
        if (!onSelectionChange) return;
        onSelectionChange({
            allSelected: allVisibleSelected,
            disabled: isLoading || visibleItems.length === 0,
            toggle: handleSelectAll,
        });
    }, [onSelectionChange, allVisibleSelected, isLoading, visibleItems.length, handleSelectAll]);

    useEffect(() => {
        onListTotalChange?.(listTotal);
    }, [onListTotalChange, listTotal]);

    /* ── Queue actions ── */
    const handleRemove = useCallback(async (docId) => {
        if (!user?.uid) return;
        await removeCardFromEbay(user.uid, docId);
    }, [user?.uid]);

    const handleUpdatePrice = useCallback(async (docId, value) => {
        if (!user?.uid) return;
        await updateEbayItem(user.uid, docId, { price: value });
    }, [user?.uid]);

    const handleToggleFormat = useCallback(async (docId, newFormat) => {
        if (!user?.uid) return;
        await updateEbayItem(user.uid, docId, { format: newFormat });
    }, [user?.uid]);


    const handleRemoveSelected = useCallback(async () => {
        if (!user?.uid || selectedIds.size === 0) return;
        if (!removePending) { setRemovePending(true); return; }
        setRemovePending(false);
        await Promise.all([...selectedIds].map((id) => removeCardFromEbay(user.uid, id)));
        setSelectedIds(new Set());
    }, [removePending, selectedIds, user?.uid]);

    /* ── Mark as listed ── */
    const handleMarkAsListed = useCallback(async () => {
        if (!user?.uid || selectedIds.size === 0 || isMarkingListed) return;
        if (!listedPending) { setListedPending(true); return; }
        setListedPending(false);
        setIsMarkingListed(true);
        try {
            const selected = items.filter((item) => selectedIds.has(item.id));
            const { saleIds, cardCount } = await promoteEbayItemsToSales(user.uid, selected);
            dispatch(api.util.invalidateTags(['Sale']));
            setSelectedIds(new Set());
            dispatch(setSystemNotification(
                `${saleIds.length} listing${saleIds.length === 1 ? '' : 's'} (${cardCount} card${cardCount === 1 ? '' : 's'}) marked as listed`
            ));
        } catch (error) {
            dispatch(setSystemNotification('Failed to mark as listed'));
        } finally {
            setIsMarkingListed(false);
        }
    }, [user?.uid, selectedIds, isMarkingListed, listedPending, items, dispatch]);

    const handleMovePlatform = useCallback(async (platform) => {
        if (!user?.uid || selectedIds.size === 0) return;
        const selected = items.filter((item) => selectedIds.has(item.id));
        try {
            await reassignStagedItems(user.uid, selected, platform);
            setSelectedIds(new Set());
            dispatch(setSystemNotification(`Moved ${selected.length} to ${platform}`));
        } catch (error) {
            dispatch(setSystemNotification('Failed to move listings'));
        }
    }, [user?.uid, selectedIds, items, dispatch]);

    /* ── Combine lot ── */
    const handleOpenCombineModal = useCallback(() => {
        if (!canCombine) return;
        setLotTitle('');
        setCombineModalOpen(true);
    }, [canCombine]);

    const handleConfirmCombine = useCallback(async () => {
        if (!user?.uid || !lotTitle.trim() || selectedSingles.length < 2 || isCombining) return;
        setIsCombining(true);
        try {
            await createEbayLot(
                user.uid,
                lotTitle.trim(),
                selectedSingles.map((item) => item.id),
                selectedSingles,
                {
                    conditionId,
                    format: defaultFormat,
                }
            );
            setCombineModalOpen(false);
            setLotTitle('');
            setSelectedIds(new Set());
        } finally {
            setIsCombining(false);
        }
    }, [user?.uid, lotTitle, selectedSingles, isCombining, conditionId, defaultFormat]);

    const handleUngroup = useCallback(async (lotItem) => {
        if (!user?.uid || !isLotItem(lotItem)) return;
        await ungroupEbayLot(user.uid, lotItem);
        setSelectedIds((prev) => {
            if (!prev.has(lotItem.id)) return prev;
            const next = new Set(prev);
            next.delete(lotItem.id);
            return next;
        });
    }, [user?.uid]);

    /* ── Add card ── */
    const handleOpenAddForm = useCallback(() => {
        setNewCard(BLANK_CARD);
        setAddFormOpen(true);
    }, []);

    const handleConfirmAdd = useCallback(async () => {
        if (!user?.uid || newCard.name.length === 0 || isAdding) return;
        setIsAdding(true);
        try {
            const cardFields = {
                series: newCard.series.trim() || null,
                name: newCard.name,
                team: newCard.team,
                cardNumber: newCard.cardNumber.trim() || null,
                cardParallelName: newCard.cardParallelName.trim() || null,
                cardAttributes: newCard.cardAttributes,
                ...parseSerial(newCard.serial),
            };
            await addCustomCardToEbay(user.uid, cardFields, { categoryId, conditionId, defaultFormat, defaultPrice });
            setAddFormOpen(false);
            setNewCard(BLANK_CARD);
        } finally {
            setIsAdding(false);
        }
    }, [user?.uid, newCard, isAdding, categoryId, conditionId, defaultFormat, defaultPrice]);

    /* ── Price insights ── */
    const handleSoldListings = useCallback((item) => {
        const url = buildEbaySearchUrl(item, item, item.series);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    const handleProductResearch = useCallback((item) => {
        const url = buildEbayResearchUrl(item, item, item.series);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    /* ── Export ── */
    const exportItems = useMemo(
        () => selectedIds.size > 0
            ? items.filter((item) => selectedIds.has(item.id))
            : items,
        [items, selectedIds]
    );

    const handleExport = useCallback(async () => {
        if (exportItems.length === 0 || isExporting) return;
        setIsExporting(true);
        try {
            // Items staged before SKUs existed get one now, so every exported
            // listing carries a custom label that orders can be matched against.
            const assigned = await ensureEbaySkus(user?.uid, exportItems);
            const itemsWithSkus = exportItems.map((item) => (
                assigned[item.id] ? { ...item, sku: assigned[item.id] } : item
            ));
            const csv = generateEbayCsv(itemsWithSkus);
            const date = new Date().toISOString().slice(0, 10);
            downloadCsv(csv, `ebay-drafts-${date}.csv`);
        } catch (error) {
            dispatch(setSystemNotification('Failed to export listings'));
        } finally {
            setIsExporting(false);
        }
    }, [exportItems, isExporting, user?.uid, dispatch]);

    const isAuctionDefault = defaultFormat === 'Auction';
    const exportCount = exportItems.length;
    const exportingSelection = selectedIds.size > 0;

    /* ── Render ── */
    return (
        <EbayPageContainer className='widget-bg'>
            <ConfigPanel>
                <ConfigFieldsContainer>
                    <img src={ebayLogo} alt="eBay" />
                    <InputDropdown
                        id="eb-category"
                        label="Category"
                        value={categoryId}
                        onChange={(v) => setCategoryId(v)}
                    >
                        {EBAY_CATEGORIES.map((c) => (
                            <option key={c.id} value={c.id}>{c.label} ({c.id})</option>
                        ))}
                    </InputDropdown>
                    <InputDropdown
                        id="eb-condition"
                        label="Condition"
                        value={conditionId}
                        onChange={(v) => setConditionId(v)}
                    >
                        {EBAY_CONDITIONS.map((c) => (
                            <option key={c.id} value={c.id}>{c.label} ({c.id})</option>
                        ))}
                    </InputDropdown>
                    <FormatToggleConfig
                        $auction={isAuctionDefault}
                        onClick={() => setDefaultFormat(isAuctionDefault ? 'FixedPrice' : 'Auction')}
                        title="Toggle default listing format"
                    >
                        {isAuctionDefault ? 'Auction' : 'Fixed Price'}
                    </FormatToggleConfig>
                    <div style={{ width: '160px' }}>
                        <InputBar
                            id="eb-default-price"
                            type="number"
                            label="Default Price ($)"
                            value={defaultPrice}
                            updateValue={setDefaultPrice}
                            pH="1"
                        />
                    </div>

                </ConfigFieldsContainer>
                <ConfigActions>
                    <AddCardBtn onClick={handleOpenAddForm}>
                        <IconPlus size={14} />
                        Add Card
                    </AddCardBtn>
                </ConfigActions>
            </ConfigPanel>

            {isLoading && <LoadingState>Loading...</LoadingState>}

            {!isLoading && items.length === 0 && (
                <EmptyState>
                    No listings staged yet
                    <p>Add cards from the checklist, or use "Add Card" above.</p>
                </EmptyState>
            )}

            {!isLoading && items.length > 0 && (
                <>
                    <QueueHeader>
                        <QueueHeaderActions>
                            {selectedIds.size > 0 && (
                                <>
                                    <CombineLotBtn
                                        type="button"
                                        onClick={handleMarkAsListed}
                                        disabled={isMarkingListed}
                                        title={listedPending
                                            ? 'Confirm: mark listed and clear from queue'
                                            : `Mark ${selectedIds.size} selected as listed on eBay`}
                                    >
                                        {listedPending ? 'Confirm Listed?' : 'Listed'}
                                    </CombineLotBtn>
                                    <select
                                        value=""
                                        aria-label="Move selected to another platform"
                                        onChange={(e) => { if (e.target.value) handleMovePlatform(e.target.value); }}
                                        style={{ height: 32, borderRadius: 6, background: '#1a1a1a', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                                    >
                                        <option value="">Move to…</option>
                                        <option value="Whatnot">Whatnot</option>
                                        <option value="Discord">Discord</option>
                                    </select>
                                    <CombineLotBtn
                                        onClick={handleOpenCombineModal}
                                        disabled={!canCombine}
                                        title={canCombine ? 'Combine selected singles into a lot' : 'Select 2 or more singles to combine'}
                                    >
                                        Create Lot
                                    </CombineLotBtn>
                                </>
                            )}
                            <ExportBtn
                                onClick={handleExport}
                                disabled={exportCount === 0 || isExporting}
                                title={exportingSelection
                                    ? `Export ${exportCount} selected listing${exportCount === 1 ? '' : 's'}`
                                    : `Export all ${exportCount} listing${exportCount === 1 ? '' : 's'}`}
                            >
                                Export CSV {!isLoading && (
                                    <ItemCount>
                                        {exportCount} {exportingSelection ? 'selected' : (exportCount === 1 ? 'listing' : 'listings')}
                                    </ItemCount>
                                )}
                            </ExportBtn>
                            {selectedIds.size > 0 && (
                                <PrimaryBtn
                                    type="critical"
                                    title={removePending
                                        ? 'CONFIRM REMOVAL?'
                                        : items.every((i) => selectedIds.has(i.id))
                                            ? 'REMOVE ALL'
                                            : 'REMOVE SELECTED'}
                                    onClick={handleRemoveSelected}
                                />
                            )}
                        </QueueHeaderActions>
                    </QueueHeader>

                    {visibleItems.length === 0 ? (
                        <EmptyState>
                            No listings match "{playerSearch.trim()}"
                            <p>Try a different player name, or press Escape to clear search.</p>
                        </EmptyState>
                    ) : (
                        <ListingPanel
                            items={visibleItems}
                            onRemove={handleRemove}
                            onUngroup={handleUngroup}
                            onUpdatePrice={handleUpdatePrice}
                            onToggleFormat={handleToggleFormat}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onSoldListings={handleSoldListings}
                            onProductResearch={handleProductResearch}
                        />
                    )}
                </>
            )}

            {/* Add card modal */}
            {addFormOpen && (
                <ModalOverlay onClick={() => !isAdding && setAddFormOpen(false)}>
                    <ModalCard $wide onClick={(e) => e.stopPropagation()}>
                        <h2>Add Card to eBay Queue</h2>
                        <InputBar id="eb-nc-series" type="text" label="Series" value={newCard.series} updateValue={(v) => setNewCard((c) => ({ ...c, series: v }))} pH="e.g. 2024 Topps Chrome" autoFocus />
                        <ModalFieldsGrid $cols={2}>
                            <MultiResponseInput id="eb-nc-name" label="Player(s)" pH="Add name, press Enter" values={newCard.name} onChange={(v) => setNewCard((c) => ({ ...c, name: v }))} />
                            <MultiResponseInput id="eb-nc-team" label="Team(s)" pH="Add team, press Enter" values={newCard.team} onChange={(v) => setNewCard((c) => ({ ...c, team: v }))} />
                        </ModalFieldsGrid>
                        <ModalFieldsGrid>
                            <InputBar id="eb-nc-num" type="text" label="Card #" value={newCard.cardNumber} updateValue={(v) => setNewCard((c) => ({ ...c, cardNumber: v }))} pH="e.g. 42" />
                            <InputBar id="eb-nc-parallel" type="text" label="Parallel" value={newCard.cardParallelName} updateValue={(v) => setNewCard((c) => ({ ...c, cardParallelName: v }))} pH="e.g. Gold Refractor" />
                            <InputBar id="eb-nc-serial" type="text" label="Serial #" value={newCard.serial} updateValue={(v) => setNewCard((c) => ({ ...c, serial: v }))} pH="e.g. 1/10" />
                        </ModalFieldsGrid>
                        <MultiResponseInput id="eb-nc-attrs" label="Card Attributes" pH="e.g. Rookie, Auto — press Enter to add" values={newCard.cardAttributes} onChange={(v) => setNewCard((c) => ({ ...c, cardAttributes: v }))} />
                        <ModalActions>
                            <ModalCancelBtn onClick={() => setAddFormOpen(false)} disabled={isAdding}>Cancel</ModalCancelBtn>
                            <ModalCreateBtn onClick={handleConfirmAdd} disabled={newCard.name.length === 0 || isAdding}>
                                {isAdding ? 'Adding...' : 'Add to Queue'}
                            </ModalCreateBtn>
                        </ModalActions>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Combine lot modal */}
            {combineModalOpen && (
                <ModalOverlay onClick={() => !isCombining && setCombineModalOpen(false)}>
                    <ModalCard $wide onClick={(e) => e.stopPropagation()}>
                        <h2>Create Lot</h2>
                        <InputBar
                            id="eb-lot-title"
                            type="text"
                            label="Lot Title"
                            value={lotTitle}
                            updateValue={setLotTitle}
                            pH="Enter lot title"
                            autoFocus
                        />
                        <div>
                            <DescriptionPreviewLabel>Description preview</DescriptionPreviewLabel>
                            <DescriptionPreview>
                                {combinePreviewDescription.replace(/<br>/g, '\n')}
                            </DescriptionPreview>
                        </div>
                        <ModalActions>
                            <ModalCancelBtn onClick={() => setCombineModalOpen(false)} disabled={isCombining}>Cancel</ModalCancelBtn>
                            <ModalCreateBtn
                                onClick={handleConfirmCombine}
                                disabled={!lotTitle.trim() || selectedSingles.length < 2 || isCombining}
                            >
                                {isCombining ? 'Creating...' : 'Create Lot'}
                            </ModalCreateBtn>
                        </ModalActions>
                    </ModalCard>
                </ModalOverlay>
            )}

        </EbayPageContainer>
    );
}
